<template>
  <div>
    <!-- Tabs - only show Messages tab when editing existing contact -->
    <ul class="nav nav-tabs nav-tabs-sm mb-3" v-if="model.Id">
        <li class="nav-item">
            <a 
                class="nav-link" 
                :class="{ active: activeTab === 'details' }"
                href="#"
                @click.prevent="activeTab = 'details'"
            >
                Details
            </a>
        </li>
        <li class="nav-item">
            <a 
                class="nav-link" 
                :class="{ active: activeTab === 'messages' }"
                href="#"
                @click.prevent="activeTab = 'messages'"
            >
                <MessageSquare :size="14" class="me-1" />
                Messages
            </a>
        </li>
    </ul>

    <!-- Details Tab -->
    <div v-show="activeTab === 'details'">
        <div class="row">
            <div class="col">
                <kt-input 
                    v-model="model.Name" 
                    label="Contact Name" 
                    placeholder="Enter contact name"
                ></kt-input>
            </div>
        </div>

        <div class="row my-2">
            <div class="col">
                <kt-input 
                    v-model="model.Email" 
                    label="Email" 
                    placeholder="Enter email address"
                ></kt-input>
            </div>
        </div>

        <div class="row my-2">
            <div class="col">
                <kt-input 
                    v-model="model.Phone" 
                    label="Primary Phone" 
                    placeholder="Enter primary phone number"
                ></kt-input>
            </div>
        </div>

        <!-- Additional Phone Numbers -->
        <div class="row my-2">
            <div class="col">
                <label class="form-label"><strong>Additional Phone Numbers</strong></label>
                <div class="phone-numbers-container">
                    <div 
                        v-for="(phone, index) in phoneNumbers" 
                        :key="index"
                        class="d-flex align-items-center gap-2 mb-2"
                    >
                        <input 
                            type="text" 
                            class="form-control form-control-sm flex-grow-1"
                            v-model="phone.number"
                            placeholder="Phone number"
                        />
                        <select 
                            class="form-select form-select-sm" 
                            style="width: 120px;"
                            v-model="phone.type"
                        >
                            <option value="mobile">Mobile</option>
                            <option value="landline">Landline</option>
                        </select>
                        <button 
                            type="button" 
                            class="btn btn-sm btn-outline-danger"
                            @click="removePhoneNumber(index)"
                            title="Remove phone number"
                        >
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                    <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="addPhoneNumber"
                    >
                        <i class="fa fa-plus me-1"></i>
                        Add Phone Number
                    </button>
                </div>
            </div>
        </div>

        <div class="row my-2">
            <div class="col">
                <!-- Contact / Client Map 
                <kt-select
                    label="Client"
                    multiple
                ></kt-select>
                {{ model.ClientMap }}
                -->
                <client-multi 
                    v-model="model.ClientMap"
                ></client-multi>
            </div>
        </div>

        <div class="row my-2">
            <div class="col">
                <kt-input 
                    type="password" 
                    v-model="model.Password" 
                    label="Password" 
                    placeholder="Leave blank to keep the same"
                ></kt-input>
            </div>
        </div>

         <!-- TODO 
        <div class="row mt-2">
            <div class="col">
                <div class="form-check">
                    <input type="checkbox" class="form-check-input" v-model="model.Status" id="flexCheckDefault" :value="1">
                    <label class="form-check-label" for="flexCheckDefault">
                        Is Active?
                    </label>
                </div>
            </div>
        </div>
        -->
        
        <!-- Profile Image Section -->
        <div class="row mt-4" v-if="model.Id">
            <div class="col">
                <h6 class="text-muted mb-2">Profile Image</h6>
                
                <!-- Display existing image -->
                <div v-if="profileImage" class="mb-3">
                    <img 
                        :src="AttachmentService.getDownloadUrl(profileImage.Id)" 
                        alt="Profile Image"
                        class="img-thumbnail"
                        style="max-width: 200px; max-height: 200px; object-fit: cover;"
                    />
                    <div class="mt-2">
                        <button 
                            @click="deleteImage()" 
                            class="btn btn-sm btn-outline-danger me-2"
                        >
                            <i class="fa fa-trash"></i> Delete Image
                        </button>
                        <label class="btn btn-sm btn-outline-primary">
                            <i class="fa fa-upload"></i> Replace Image
                            <input 
                                type="file" 
                                @change="handleImageUpload($event)" 
                                accept="image/*"
                                style="display: none;"
                            />
                        </label>
                    </div>
                </div>

                <!-- Upload new image -->
                <div v-else>
                    <label class="btn btn-sm btn-outline-primary">
                        <i class="fa fa-upload"></i> Upload Profile Image
                        <input 
                            type="file" 
                            @change="handleImageUpload($event)" 
                            accept="image/*"
                            style="display: none;"
                        />
                    </label>
                    <p class="text-muted small mt-2">Supported formats: JPG, PNG, GIF (max 5MB)</p>
                </div>
            </div>
        </div>
    </div>

    <!-- Messages Tab -->
    <div v-show="activeTab === 'messages'" v-if="model.Id">
        <staff-messages 
            :primary-phone="model.Phone"
            :additional-phones="phoneNumbers"
        />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useRoute } from "vue-router"
import ApiService from "@/services/ApiService";
import Alert from "@/services/Alert";
// import StaffModel from '@/models/StaffModel';
import KtInput from '@/components/UI/KtInput.vue';
import KtUpload from '@/components/UI/KtUpload.vue';
import Helper from '@/services/Helper';
import Api from '@/services/ApiService';
import AttachmentService from '@/services/AttachmentService';
import ClientMulti from "@/components/Clients/ClientMulti.vue";
import StaffMessages from "@/components/Staff/StaffMessages.vue";
import { MessageSquare } from "lucide-vue-next";

const route = useRoute();
const activeTab = ref('details');

// Phone entry type
const createPhoneEntry = (number = '', type = 'mobile') => ({ number, type });

const props = defineProps(
    {
        clients: {
            type: Array,
            default: []
        }
    }
);

// const model = ref({});
const model = defineModel({
    type: Object,
    default: {}
});

const profileImage = ref(null);

// Phone numbers management - synced with model.AdditionalData.phoneNumbers
const phoneNumbers = computed({
    get: () => {
        // Parse AdditionalData if it's a string
        let additionalData = model.value.AdditionalData;
        if (typeof additionalData === 'string') {
            try {
                additionalData = JSON.parse(additionalData);
            } catch {
                additionalData = null;
            }
        }
        return additionalData?.phoneNumbers || [];
    },
    set: (val) => {
        if (!model.value.AdditionalData || typeof model.value.AdditionalData === 'string') {
            model.value.AdditionalData = {};
        }
        model.value.AdditionalData.phoneNumbers = val;
    }
});

const addPhoneNumber = () => {
    // Ensure AdditionalData is an object
    if (!model.value.AdditionalData || typeof model.value.AdditionalData === 'string') {
        model.value.AdditionalData = { phoneNumbers: [] };
    }
    if (!model.value.AdditionalData.phoneNumbers) {
        model.value.AdditionalData.phoneNumbers = [];
    }
    model.value.AdditionalData.phoneNumbers.push(createPhoneEntry());
};

const removePhoneNumber = (index) => {
    if (model.value.AdditionalData?.phoneNumbers) {
        model.value.AdditionalData.phoneNumbers.splice(index, 1);
    }
};

// Parse AdditionalData from JSON string when model loads
watch(() => model.value.AdditionalData, (newVal) => {
    if (typeof newVal === 'string' && newVal) {
        try {
            model.value.AdditionalData = JSON.parse(newVal);
        } catch {
            model.value.AdditionalData = null;
        }
    }
}, { immediate: true });

// Load profile image when model.Id changes
watch(() => model.value.Id, async (newId) => {
    if (newId) {
        await loadProfileImage();
    }
}, { immediate: true });

const loadProfileImage = async () => {
    if (!model.value.Id) return;
    
    try {
        const response = await ApiService.loadTechnicianImage(model.value.Id);
        profileImage.value = response || null;
    } catch (error) {
        console.error('Error loading profile image:', error);
        profileImage.value = null;
    }
};

const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
        Alert.notify({
            text: 'Image size must be less than 5MB',
            type: 'error'
        });
        return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
        Alert.notify({
            text: 'Please select a valid image file',
            type: 'error'
        });
        return;
    }

    try {
        const response = await ApiService.uploadProfileImage(file, 'technician', model.value.Id);
        profileImage.value = response;
        Alert.notify({
            text: 'Profile image uploaded successfully!',
            type: 'success'
        });
    } catch (error) {
        console.error('Error uploading image:', error);
        Alert.notify({
            text: 'Error uploading image',
            type: 'error'
        });
    }

    // Reset file input
    event.target.value = '';
};

const deleteImage = async () => {
    if (!profileImage.value) return;

    Alert.confirm('Are you sure you want to delete this profile image?', async () => {
        try {
            await ApiService.deleteProfileImage(profileImage.value.Id);
            profileImage.value = null;
            Alert.notify({
                text: 'Profile image deleted successfully!',
                type: 'success'
            });
        } catch (error) {
            console.error('Error deleting image:', error);
            Alert.notify({
                text: 'Error deleting image',
                type: 'error'
            });
        }
    });
};

onMounted(async () =>
{
    if (props.clients.length)
    {

    } 
});

</script>